import { apiSlice } from "../api/apiSlice";
import { decodeId } from "../../utils/idObfuscator";

const resolveId = (id) => {
  if (!id) return "";
  const decoded = decodeId(id);
  return decoded || id;
};

export const shopMemberApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getShopMembers: builder.query({
      query: (shopId) => {
        const cleanShopId = resolveId(shopId);
        return cleanShopId
          ? `/shop/${encodeURIComponent(cleanShopId)}/members`
          : "/shop/members";
      },
      providesTags: (_result, _error, shopId) => [
        { type: "Shop", id: `MEMBERS-${shopId}` },
      ],
    }),
    getShopMemberInvitation: builder.query({
      query: (arg) => {
        const membershipId =
          typeof arg === "object" && arg !== null ? arg.membershipId : arg;
        const cleanMembershipId = resolveId(membershipId);
        const token =
          typeof arg === "object" && arg !== null ? arg.token : undefined;
        return {
          url: `/shop/member-invitations/${encodeURIComponent(cleanMembershipId)}`,
          params: token ? { token } : undefined,
        };
      },
      providesTags: (_result, _error, arg) => {
        const membershipId =
          typeof arg === "object" && arg !== null ? arg.membershipId : arg;
        return [{ type: "Shop", id: `INVITATION-${membershipId}` }];
      },
    }),
    respondToShopMemberInvitation: builder.mutation({
      query: ({ membershipId, action, password, token }) => {
        const cleanMembershipId = resolveId(membershipId);
        return {
          url: `/shop/member-invitations/${encodeURIComponent(
            cleanMembershipId,
          )}/${action}`,
          method: "POST",
          params: token ? { token } : undefined,
          body: {
            ...(password ? { password } : {}),
            ...(token ? { token } : {}),
          },
        };
      },
      invalidatesTags: (_result, _error, { membershipId }) => [
        { type: "Shop", id: `INVITATION-${membershipId}` },
        { type: "Shop", id: "MANAGED" },
      ],
      async onQueryStarted({ membershipId, action }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          if (typeof window !== "undefined") {
            const payload = {
              type:
                action === "accept"
                  ? "INVITATION_ACCEPTED"
                  : "INVITATION_UPDATED",
              membershipId,
              timestamp: Date.now(),
            };
            try {
              localStorage.setItem(
                "last_shop_membership_action",
                JSON.stringify(payload),
              );
            } catch (e) {}
            window.dispatchEvent(
              new CustomEvent("shop_invitation_accepted", { detail: payload }),
            );
            if (typeof BroadcastChannel !== "undefined") {
              try {
                const bc = new BroadcastChannel("shop_membership_channel");
                bc.postMessage(payload);
                bc.close();
              } catch (e) {}
            }
          }
        } catch {}
      },
    }),
    saveShopMemberAccess: builder.mutation({
      query: ({ shopId, userId, role, accessAreas }) => {
        const cleanShopId = resolveId(shopId);
        return {
          url: cleanShopId
            ? `/shop/${encodeURIComponent(cleanShopId)}/members/access`
            : "/shop/members/access",
          method: "POST",
          body: {
            userId,
            role,
            accessAreas,
            ...(cleanShopId ? { shopId: cleanShopId } : {}),
          },
        };
      },
      invalidatesTags: (_result, _error, { shopId }) => {
        const cleanShopId = resolveId(shopId);
        return [
          { type: "Shop", id: `MEMBERS-${shopId}` },
          ...(cleanShopId && cleanShopId !== shopId
            ? [{ type: "Shop", id: `MEMBERS-${cleanShopId}` }]
            : []),
          { type: "Shop", id: "MANAGED" },
        ];
      },
    }),
    removeShopMember: builder.mutation({
      query: ({ shopId, membershipId }) => {
        const cleanShopId = resolveId(shopId);
        const cleanMembershipId = resolveId(membershipId);
        return {
          url: cleanShopId
            ? `/shop/${encodeURIComponent(cleanShopId)}/members/${encodeURIComponent(
                cleanMembershipId,
              )}`
            : `/shop/members/${encodeURIComponent(cleanMembershipId)}`,
          method: "DELETE",
        };
      },
      async onQueryStarted(
        { shopId, membershipId },
        { dispatch, queryFulfilled },
      ) {
        const cleanShopId = resolveId(shopId);
        const targetId = String(resolveId(membershipId) || membershipId);

        const cacheKeys = Array.from(
          new Set([shopId, cleanShopId].filter(Boolean)),
        );
        const patches = cacheKeys.map((key) =>
          dispatch(
            shopMemberApiSlice.util.updateQueryData(
              "getShopMembers",
              key,
              (draft) => {
                if (draft?.data?.members && Array.isArray(draft.data.members)) {
                  draft.data.members = draft.data.members.filter(
                    (m) =>
                      String(m._id) !== targetId &&
                      String(m.id) !== targetId,
                  );
                }
              },
            ),
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: (_result, _error, { shopId }) => {
        const cleanShopId = resolveId(shopId);
        return [
          { type: "Shop", id: `MEMBERS-${shopId}` },
          ...(cleanShopId && cleanShopId !== shopId
            ? [{ type: "Shop", id: `MEMBERS-${cleanShopId}` }]
            : []),
        ];
      },
    }),
    checkShopMemberUsernameAvailability: builder.query({
      query: ({ shopId, username, membershipId }) => {
        const cleanShopId = resolveId(shopId);
        const cleanMembershipId = resolveId(membershipId);
        return {
          url: cleanShopId
            ? `/shop/${encodeURIComponent(cleanShopId)}/members/username-availability`
            : "/shop/members/username-availability",
          params: {
            username,
            ...(cleanMembershipId ? { membershipId: cleanMembershipId } : {}),
            ...(cleanShopId ? { shopId: cleanShopId } : {}),
          },
        };
      },
    }),
    checkShopMemberPasswordAvailability: builder.query({
      query: ({ shopId, password, membershipId }) => {
        const cleanShopId = resolveId(shopId);
        const cleanMembershipId = resolveId(membershipId);
        return {
          url: cleanShopId
            ? `/shop/${encodeURIComponent(cleanShopId)}/members/password-availability`
            : "/shop/members/password-availability",
          params: {
            password,
            ...(cleanMembershipId ? { membershipId: cleanMembershipId } : {}),
            ...(cleanShopId ? { shopId: cleanShopId } : {}),
          },
        };
      },
    }),
    setShopMemberCredentials: builder.mutation({
      query: ({ shopId, membershipId, username, password }) => {
        const cleanShopId = resolveId(shopId);
        const cleanMembershipId = resolveId(membershipId);
        return {
          url: cleanShopId
            ? `/shop/${encodeURIComponent(cleanShopId)}/members/${encodeURIComponent(
                cleanMembershipId,
              )}/credentials`
            : `/shop/members/${encodeURIComponent(cleanMembershipId)}/credentials`,
          method: "PATCH",
          body: {
            username,
            password,
            ...(cleanShopId ? { shopId: cleanShopId } : {}),
          },
        };
      },
      invalidatesTags: (_result, _error, { shopId }) => [
        { type: "Shop", id: `MEMBERS-${shopId}` },
      ],
    }),
    shareShopMemberInvitation: builder.mutation({
      query: ({ shopId, membershipId }) => {
        const cleanShopId = resolveId(shopId);
        const cleanMembershipId = resolveId(membershipId);
        return {
          url: cleanShopId
            ? `/shop/${encodeURIComponent(cleanShopId)}/members/${encodeURIComponent(
                cleanMembershipId,
              )}/share`
            : `/shop/members/${encodeURIComponent(cleanMembershipId)}/share`,
          method: "POST",
          body: cleanShopId ? { shopId: cleanShopId } : undefined,
        };
      },
    }),
    getMyManagedShops: builder.query({
      query: (params) => ({
        url: "/shop/managed-shops",
        params,
      }),
      providesTags: [{ type: "Shop", id: "MANAGED" }],
    }),
  }),
});

export const {
  useGetShopMembersQuery,
  useGetShopMemberInvitationQuery,
  useRespondToShopMemberInvitationMutation,
  useRemoveShopMemberMutation,
  useSaveShopMemberAccessMutation,
  useLazyCheckShopMemberUsernameAvailabilityQuery,
  useLazyCheckShopMemberPasswordAvailabilityQuery,
  useSetShopMemberCredentialsMutation,
  useShareShopMemberInvitationMutation,
  useGetMyManagedShopsQuery,
} = shopMemberApiSlice;
